// src/pages/subscriptions/BranchSubscribersManager.jsx
//
// إدارة المشتركين اليدويين (اللي بيتضافوا من غير دفع) في الأربع فروع:
// IELTS / STEP / ESP (All Tests) / General English
//
// بيستخدم نفس الـ endpoints اللي اتضافت في subscription_views.py لكل فرع:
//   GET    /<branch>/subscription/admin/list/
//   POST   /<branch>/subscription/admin/create/
//   PATCH  /<branch>/subscription/admin/<id>/update/
//   DELETE /<branch>/subscription/admin/<id>/delete/
// وللفروع اللي فيها كاتيجوري (esp, general):
//   GET    /<branch>/categories/
//   GET    /<branch>/categories/<id>/subscription/plans/all/
// وللفروع من غير كاتيجوري (ielts, step):
//   GET    /<branch>/subscription/plans/all/

import { useState, useEffect, useCallback } from "react";
import {
  Search,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  User,
  Calendar,
  CreditCard,
  RefreshCw,
  Plus,
  Pencil,
  Gift,
  ChevronRight,
  ChevronLeft,
  Layers,
  Mail,
} from "lucide-react";
import api from "../../api/axios";

// ─────────────────────────────────────────
// إعدادات الفروع
// ─────────────────────────────────────────
const BRANCHES = [
  { key: "ielts", label: "IELTS", accent: "#f97316", hasCategory: false },
  { key: "step", label: "STEP", accent: "#4f7cff", hasCategory: false },
  { key: "general", label: "General English", accent: "#10b981", hasCategory: true },
];

const STATUS_OPTIONS = [
  ["all", "الكل"],
  ["paid", "مفعّل"],
  ["pending", "معلق"],
  ["failed", "فشل"],
];

// ─────────────────────────────────────────
// Toast
// ─────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed top-5 left-5 z-[999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-white text-sm font-semibold ${
        type === "success" ? "bg-emerald-500" : "bg-rose-500"
      }`}
    >
      {type === "success" ? (
        <CheckCircle className="w-5 h-5" />
      ) : (
        <AlertCircle className="w-5 h-5" />
      )}
      {message}
      <button onClick={onClose}>
        <X className="w-4 h-4 opacity-60 hover:opacity-100" />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────
// Status Badge
// ─────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    paid: { label: "مفعّل", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
    pending: { label: "معلق", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
    failed: { label: "فشل", cls: "bg-rose-50 text-rose-700 border border-rose-200" },
  };
  const s = map[status] || { label: status, cls: "bg-gray-100 text-gray-500" };
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-lg whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

function fmtDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("ar-SA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// نحول أي تاريخ لصيغة datetime-local عشان الـ input
function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

// ─────────────────────────────────────────
// Add Subscriber Modal
// ─────────────────────────────────────────
function AddSubscriberModal({ branch, categories, onClose, onCreated, showToast }) {
  const [studentInput, setStudentInput] = useState("");
  const [categoryId, setCategoryId] = useState(categories?.[0]?.id ?? "");
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planId, setPlanId] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPlans = useCallback(
    async (catId) => {
      setPlansLoading(true);
      setPlanId("");
      setPlans([]);
      try {
        const url = branch.hasCategory
          ? `/${branch.key}/categories/${catId}/subscription/plans/all/`
          : `/${branch.key}/subscription/plans/all/`;
        const res = await api.get(url);
        const list = res.data?.plans || res.data?.results || (Array.isArray(res.data) ? res.data : []);
        setPlans(list);
        if (list.length) setPlanId(String(list[0].id));
      } catch {
        setPlans([]);
      } finally {
        setPlansLoading(false);
      }
    },
    [branch]
  );

  useEffect(() => {
    if (branch.hasCategory) {
      if (categoryId) loadPlans(categoryId);
    } else {
      loadPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, categoryId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!studentInput.trim()) {
      setError("لازم تدخل إيميل أو رقم الطالب");
      return;
    }
    if (!planId) {
      setError("لازم تختار خطة اشتراك");
      return;
    }

    const isEmail = studentInput.includes("@");
    const payload = {
      plan_id: Number(planId),
      ...(isEmail ? { student_email: studentInput.trim() } : { student_id: Number(studentInput.trim()) }),
      ...(durationDays ? { duration_days: Number(durationDays) } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    };

    setLoading(true);
    try {
      await api.post(`/${branch.key}/subscription/admin/create/`, payload);
      onCreated();
      showToast(`✅ تم تفعيل اشتراك ${branch.label} للطالب بنجاح`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.plan_id ||
        err?.response?.data?.duration_days ||
        "تعذر إضافة الاشتراك، تأكد من البيانات";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find((p) => String(p.id) === String(planId));

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7" dir="rtl">
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${branch.accent}1a` }}
          >
            <Gift className="w-5 h-5" style={{ color: branch.accent }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">إضافة مشترك يدوياً</h3>
            <p className="text-xs text-gray-400">{branch.label} — من غير دفع</p>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold px-3.5 py-2.5 rounded-xl mb-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">
              إيميل الطالب أو رقمه (ID)
            </label>
            <div className="relative">
              <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={studentInput}
                onChange={(e) => setStudentInput(e.target.value)}
                placeholder="student@email.com أو 123"
                className="w-full pr-10 pl-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                required
              />
            </div>
          </div>

          {branch.hasCategory && (
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5">الكاتيجوري</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                required
              >
                {(!categories || categories.length === 0) && (
                  <option value="">لا توجد كاتيجوريز</option>
                )}
                {categories?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">خطة الاشتراك</label>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              disabled={plansLoading || plans.length === 0}
              required
            >
              {plansLoading && <option>...جاري التحميل</option>}
              {!plansLoading && plans.length === 0 && <option value="">لا توجد خطط متاحة</option>}
              {!plansLoading &&
                plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || p.plan_type} — {p.duration_days} يوم
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">
              مدة الاشتراك (بالأيام) — اختياري
            </label>
            <input
              type="number"
              min="1"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              placeholder={selectedPlan ? `افتراضي: ${selectedPlan.duration_days} يوم` : "افتراضي حسب الخطة"}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">ملاحظة — اختياري</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="مثال: هدية من الإدارة"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 text-white py-3 rounded-xl text-sm font-bold disabled:opacity-60"
              style={{ background: branch.accent }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} تفعيل الاشتراك
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl text-sm font-bold"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Edit / Extend Modal
// ─────────────────────────────────────────
function EditSubscriptionModal({ branch, subscription, onClose, onUpdated, showToast }) {
  const [expiresAt, setExpiresAt] = useState(toDatetimeLocal(subscription.expires_at));
  const [paymentStatus, setPaymentStatus] = useState(subscription.payment_status);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.patch(`/${branch.key}/subscription/admin/${subscription.id}/update/`, {
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : subscription.expires_at,
        payment_status: paymentStatus,
      });
      onUpdated();
      showToast("✏️ تم تحديث الاشتراك بنجاح");
    } catch (err) {
      setError(err?.response?.data?.error || "تعذر تحديث الاشتراك");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-7" dir="rtl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-2xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Pencil className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">تعديل الاشتراك</h3>
            <p className="text-xs text-gray-400">{subscription.student?.email}</p>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold px-3.5 py-2.5 rounded-xl mb-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">تاريخ الانتهاء</label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">حالة الدفع</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            >
              <option value="paid">مفعّل</option>
              <option value="pending">معلق</option>
              <option value="failed">ملغي / فشل</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white py-3 rounded-xl text-sm font-bold"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} حفظ التعديل
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl text-sm font-bold"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Delete Modal
// ─────────────────────────────────────────
function DeleteModal({ branch, subscription, onClose, onDeleted, showToast }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/${branch.key}/subscription/admin/${subscription.id}/delete/`);
      onDeleted();
      showToast("🗑️ تم حذف الاشتراك بنجاح");
    } catch {
      showToast("تعذر حذف الاشتراك", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-7 text-center" dir="rtl">
        <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-8 h-8 text-rose-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">حذف الاشتراك</h3>
        <p className="text-gray-500 text-sm mb-6">
          هل تريد حذف اشتراك{" "}
          <span className="font-bold text-gray-800">{subscription.student?.email}</span> في{" "}
          <span className="font-bold text-gray-800">{branch.label}</span>؟
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleDelete}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white py-3 rounded-xl text-sm font-bold"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} نعم، احذف
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl text-sm font-bold"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Subscription Row
// ─────────────────────────────────────────
function SubscriptionRow({ sub, branch, onEdit, onDelete }) {
  const isGift = sub.payment_id?.startsWith("ADMIN-GIFT");

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/70 transition-colors group">
      <td className="px-4 py-3.5" dir="rtl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-indigo-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{sub.student?.email}</p>
            {sub.student?.username && (
              <p className="text-xs text-gray-400 mt-0.5">{sub.student.username}</p>
            )}
          </div>
        </div>
      </td>

      {branch.hasCategory && (
        <td className="px-4 py-3.5 text-center">
          <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-lg">
            {sub.category?.name || "—"}
          </span>
        </td>
      )}

      <td className="px-4 py-3.5 text-center">
        <span className="text-sm font-semibold text-gray-800">{sub.plan?.plan_type}</span>
      </td>

      <td className="px-4 py-3.5 text-center">
        <StatusBadge status={sub.payment_status} />
      </td>

      <td className="px-4 py-3.5 text-center">
        {isGift ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-violet-600 bg-violet-50 border border-violet-200 px-2.5 py-1 rounded-lg">
            <Gift className="w-3 h-3" /> هدية
          </span>
        ) : (
          <span className="text-sm font-bold text-gray-900">{sub.amount} ر</span>
        )}
      </td>

      <td className="px-4 py-3.5 text-center">
        <span className="text-xs text-gray-400">{fmtDate(sub.expires_at)}</span>
      </td>

      <td className="px-4 py-3.5 text-center">
        {sub.is_active ? (
          <span className="text-xs font-bold text-emerald-600">نشط</span>
        ) : (
          <span className="text-xs font-bold text-gray-400">منتهي</span>
        )}
      </td>

      <td className="px-4 py-3.5">
        <div className="flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
          <button
            onClick={() => onEdit(sub)}
            className="w-8 h-8 rounded-lg hover:bg-indigo-100 flex items-center justify-center"
            title="تعديل"
          >
            <Pencil className="w-3.5 h-3.5 text-indigo-500" />
          </button>
          <button
            onClick={() => onDelete(sub)}
            className="w-8 h-8 rounded-lg hover:bg-rose-100 flex items-center justify-center"
            title="حذف"
          >
            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────
export default function BranchSubscribersManager() {
  const [branchKey, setBranchKey] = useState("ielts");
  const branch = BRANCHES.find((b) => b.key === branchKey);

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeOnly, setActiveOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total_pages: 1, total_items: 0 });
  const [summary, setSummary] = useState({ total: 0, active: 0 });

  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'delete'
  const [selectedSub, setSelectedSub] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => setToast({ message: msg, type });

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // لما الفرع يتغير: نرجع كل الفلاتر للوضع الافتراضي
  useEffect(() => {
    setPage(1);
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setActiveOnly(false);
    setCategoryFilter("");
    setCategories([]);
    if (branch.hasCategory) fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchKey]);

  const fetchCategories = async () => {
    try {
      const res = await api.get(`/${branchKey}/categories/`);
      const list =
        res.data?.categories || res.data?.results || (Array.isArray(res.data) ? res.data : []);
      setCategories(list);
    } catch {
      setCategories([]);
    }
  };

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 10 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== "all") params.status = statusFilter;
      if (activeOnly) params.active_only = "true";
      if (branch.hasCategory && categoryFilter) params.category_id = categoryFilter;

      const res = await api.get(`/${branchKey}/subscription/admin/list/`, { params });
      setSubscriptions(res.data?.subscriptions || []);
      setPagination(res.data?.pagination || { total_pages: 1, total_items: 0 });
    } catch {
      showToast("فشل في تحميل الاشتراكات", "error");
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [branchKey, page, debouncedSearch, statusFilter, activeOnly, categoryFilter, branch.hasCategory]);

  const fetchSummary = useCallback(async () => {
    try {
      const [totalRes, activeRes] = await Promise.all([
        api.get(`/${branchKey}/subscription/admin/list/`, { params: { page_size: 1 } }),
        api.get(`/${branchKey}/subscription/admin/list/`, {
          params: { page_size: 1, active_only: "true" },
        }),
      ]);
      setSummary({
        total: totalRes.data?.pagination?.total_items ?? 0,
        active: activeRes.data?.pagination?.total_items ?? 0,
      });
    } catch {
      setSummary({ total: 0, active: 0 });
    }
  }, [branchKey]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const refreshAll = () => {
    fetchSubscriptions();
    fetchSummary();
  };

  const openAdd = () => setModal("add");
  const openEdit = (sub) => {
    setSelectedSub(sub);
    setModal("edit");
  };
  const openDelete = (sub) => {
    setSelectedSub(sub);
    setModal("delete");
  };
  const closeModal = () => {
    setModal(null);
    setSelectedSub(null);
  };

  return (
    <div className="space-y-6" dir="rtl">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {modal === "add" && (
        <AddSubscriberModal
          branch={branch}
          categories={categories}
          onClose={closeModal}
          onCreated={() => {
            closeModal();
            refreshAll();
          }}
          showToast={showToast}
        />
      )}
      {modal === "edit" && selectedSub && (
        <EditSubscriptionModal
          branch={branch}
          subscription={selectedSub}
          onClose={closeModal}
          onUpdated={() => {
            closeModal();
            refreshAll();
          }}
          showToast={showToast}
        />
      )}
      {modal === "delete" && selectedSub && (
        <DeleteModal
          branch={branch}
          subscription={selectedSub}
          onClose={closeModal}
          onDeleted={() => {
            closeModal();
            refreshAll();
          }}
          showToast={showToast}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة المشتركين</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            تفعيل ومتابعة اشتراكات الطلاب في IELTS, STEP, ESP و General يدوياً
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshAll}
            className="flex items-center gap-2 border border-gray-200 hover:border-indigo-300 text-gray-600 hover:text-indigo-600 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors bg-white"
          >
            <RefreshCw className="w-4 h-4" /> تحديث
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-md"
            style={{ background: branch.accent }}
          >
            <Plus className="w-4 h-4" /> إضافة مشترك
          </button>
        </div>
      </div>

      {/* Branch Tabs */}
      <div className="flex gap-2 bg-white border border-gray-100 rounded-2xl p-1.5 shadow-sm w-fit">
        {BRANCHES.map((b) => (
          <button
            key={b.key}
            onClick={() => setBranchKey(b.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
              branchKey === b.key ? "text-white shadow-md" : "text-gray-500 hover:bg-gray-50"
            }`}
            style={branchKey === b.key ? { background: b.accent } : {}}
          >
            <Layers className="w-3.5 h-3.5" />
            {b.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-indigo-500" />
            <span className="text-xs text-gray-500">إجمالي اشتراكات {branch.label}</span>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{summary.total}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-gray-500">اشتراكات فعالة الآن</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{summary.active}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="ابحث بالإيميل، اليوزرنيم، أو رقم العملية..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pr-11 pl-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
          />
        </div>

        {branch.hasCategory && (
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        <div className="flex gap-2">
          {STATUS_OPTIONS.map(([val, label]) => (
            <button
              key={val}
              onClick={() => {
                setStatusFilter(val);
                setPage(1);
              }}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
                statusFilter === val
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
            className="accent-indigo-600"
          />
         فعال
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
          <CreditCard className="w-14 h-14 text-gray-200 mx-auto mb-3" />
          <h3 className="text-gray-600 font-bold mb-1">
            {debouncedSearch ? "لا توجد نتائج" : `لا يوجد مشتركين في ${branch.label} بعد`}
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            {debouncedSearch ? "جرب البحث بكلمة أخرى" : "ابدأ بإضافة أول مشترك يدوياً"}
          </p>
          {!debouncedSearch && (
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: branch.accent }}
            >
              <Plus className="w-4 h-4" /> إضافة مشترك
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {[
                    "الطالب",
                    ...(branch.hasCategory ? ["الكاتيجوري"] : []),
                    "الخطة",
                    "الحالة",
                    "المبلغ",
                    "ينتهي في",
                    "نشط؟",
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-xs font-bold text-gray-500 text-right whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((sub) => (
                  <SubscriptionRow
                    key={sub.id}
                    sub={sub}
                    branch={branch}
                    onEdit={openEdit}
                    onDelete={openDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              صفحة {page} من {pagination.total_pages} — {pagination.total_items} اشتراك
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="w-8 h-8 rounded-lg border border-gray-200 bg-white disabled:opacity-40 flex items-center justify-center"
              >
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={page >= pagination.total_pages}
                className="w-8 h-8 rounded-lg border border-gray-200 bg-white disabled:opacity-40 flex items-center justify-center"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}